<script>
  import Layout from '../components/Layout.svelte';
  import ApprovalStepToggle from '../components/ApprovalStepToggle.svelte';
  import { navigate } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user, awaitingApprovalCount } from '../stores/authStore';
  import { settingsStore } from '../stores/settingsStore.js';
  import { normalizeProvince } from '../lib/canadaTax.js';
  import { buildDirectBillJtPayload } from '../lib/directBillJobTreadPayload.js';

  let loading = true;
  let error = null;
  let success = null;
  let bills = [];
  let allocations = {};
  let syncingId = null;
  let syncNote = {};
  let rowError = {};
  let partialSync = {};
  let duplicatePrompt = null;
  const costItemCache = {};

  const { error: settingsError } = settingsStore;

  $: fmt = createCurrencyFormatter($user?.country);
  $: isQuickBooks = $user?.accountingPlatform === 'quickbooks';
  $: caProvince = $user?.country === 'CA' ? normalizeProvince($user?.province) : null;
  $: approvalsOn = $user?.billSettings?.requireDirectBillApproval === true;
  $: canTurnApprovalsOn = !$user?.orgId || $user?.orgRole === 'owner' || $user?.orgRole === 'admin';

  let loadedWhileOn = false;
  $: if (approvalsOn && !loadedWhileOn) {
    loadedWhileOn = true;
    load();
  } else if (!approvalsOn) {
    loadedWhileOn = false;
    loading = false;
  }

  function openBill(bill) {
    navigate('/review-direct-bills', { state: { selectedBills: [{ uploadId: bill.uploadId }] } });
  }

  function submittedLabel(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
  }

  function cleanCostGroupName(name) {
    if (!name) return name;
    return name
      .replace(/[-\s]*#[\u4e00-\u9fff]+$/g, '')
      .replace(/[\u4e00-\u9fff]+/g, '')
      .replace(/[-\s]+$/, '')
      .trim();
  }

  function jobLabel(bill) {
    const payload = bill.reviewPayload || {};
    return payload.matchedJob?.jobName || payload.matchedPO?.jobName || '';
  }

  function referenceLabel(bill) {
    const data = bill.reviewPayload?.extractedData || bill.extractedData || {};
    const po = bill.reviewPayload?.matchedPO?.vendorOrderNumber;
    return data.poNumber || po || '';
  }

  function allocationText(line, overhead) {
    if (overhead) return 'Overhead';
    if (line.costGroupName && line.costItemName) return `${line.costGroupName} · ${line.costItemName}`;
    return line.costGroupName || line.costItemName || 'Not allocated';
  }

  function flattenCostGroups(groups) {
    const items = [];
    for (const group of groups || []) {
      if (/- Bill .+ - (Job Mode|PO #)/i.test(group.name || '')) continue;
      const groupItems = group.costItems || group.lineItems || [];
      for (const item of groupItems) {
        items.push({
          ...item,
          costGroupId: group.id,
          costGroupName: cleanCostGroupName(group.name)
        });
      }
    }
    return items;
  }

  async function loadJobCostCentres(payload) {
    if (!payload || payload.accountingOnly) return { items: [], groups: [] };
    const poMode = payload.isJobMode === false && payload.matchedPO?.vendorOrderId;
    const key = poMode
      ? `po:${payload.matchedPO.vendorOrderId}`
      : `job:${payload.matchedJob?.jobId || payload.matchedPO?.jobId || ''}`;
    if (!key.endsWith(':') && costItemCache[key]) return costItemCache[key];
    if (key.endsWith(':')) return { items: [], groups: [] };

    const url = poMode
      ? `/api/jt/lineitems?jtjobid=${encodeURIComponent(payload.matchedPO.vendorOrderId)}`
      : `/api/jt/jobs/${encodeURIComponent(payload.matchedJob?.jobId || payload.matchedPO?.jobId)}/cost-groups-with-items`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load cost centres');
    const groups = data.costGroups || [];
    const loaded = { items: flattenCostGroups(groups), groups };
    costItemCache[key] = loaded;
    return loaded;
  }

  async function costItemsFor(payload) {
    const loaded = await loadJobCostCentres(payload);
    return loaded.items;
  }

  function linesFromPayload(bill, items) {
    const payload = bill.reviewPayload || {};
    if (payload.lineAllocations?.length) return payload.lineAllocations;
    const source = payload.extractedData?.lineItems || bill.extractedData?.lineItems || [];
    const assignments = payload.lineItemAssignments || {};
    const byId = Object.fromEntries((items || []).map(item => [item.id, item]));
    return source.map((line, i) => {
      const item = byId[assignments[i]];
      return {
        description: line.description || '',
        amount: Number(line.amount) || 0,
        costItemName: item?.name || null,
        costGroupName: item?.costGroupName || null
      };
    });
  }

  async function resolveAllocations(list) {
    const next = { ...allocations };
    for (const bill of list) {
      try {
        const items = bill.reviewPayload?.lineAllocations?.length
          ? []
          : await costItemsFor(bill.reviewPayload || {});
        next[bill.uploadId] = linesFromPayload(bill, items);
      } catch (_) {
        next[bill.uploadId] = linesFromPayload(bill, []);
      }
    }
    allocations = next;
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const res = await fetch('/api/direct-bills/list?status=awaiting_approval');
      const data = await res.json();
      if (res.ok && data.success) {
        bills = (data.uploads || []).slice().sort((a, b) => {
          const aTime = Date.parse(a.submittedForApprovalAt || '') || 0;
          const bTime = Date.parse(b.submittedForApprovalAt || '') || 0;
          return bTime - aTime;
        });
        awaitingApprovalCount.set(bills.length);
        resolveAllocations(bills);
      } else {
        error = data.error || 'Failed to load approvals';
      }
    } catch (e) {
      error = 'Failed to load approvals. ' + e.message;
    } finally {
      loading = false;
    }
  }

  function enrichmentFor(bill) {
    const payload = bill.reviewPayload || {};
    const enrichment = { ...(payload.enrichment || {}) };
    const entry = { ...(enrichment[bill.uploadId] || {}) };
    if (!entry.xeroContact && payload.xeroContact) entry.xeroContact = payload.xeroContact;
    if (!entry.job && payload.accountingOnly !== true) {
      const source = payload.matchedJob || payload.matchedPO;
      if (source && (source.jobId || source.jobName || source.jobNumber)) {
        entry.job = {
          jobId: source.jobId || null,
          jobName: source.jobName || '',
          jobNumber: source.jobNumber != null && source.jobNumber !== '' ? String(source.jobNumber) : ''
        };
      }
    }
    enrichment[bill.uploadId] = entry;
    return enrichment;
  }

  async function postXero(bill, allowDuplicate) {
    const response = await fetch('/api/direct-bills/sync-to-xero', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadIds: [bill.uploadId],
        enrichment: enrichmentFor(bill),
        ...(allowDuplicate ? { allowDuplicateFor: [bill.uploadId] } : {})
      })
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  }

  async function postJobTread(bill, xeroBill) {
    const payload = bill.reviewPayload || {};
    const extracted = payload.extractedData || bill.extractedData || {};
    const centres = await loadJobCostCentres(payload);
    const body = buildDirectBillJtPayload({
      extractedData: extracted,
      lineItemAssignments: payload.lineItemAssignments || {},
      costItems: centres.items,
      isJobMode: payload.isJobMode !== false,
      matchedJob: payload.matchedJob,
      matchedPO: payload.matchedPO,
      selectedVendorId: payload.selectedVendorId,
      markAsPaid: payload.markAsPaid === true,
      upload: bill,
      xeroBillId: xeroBill?.id || null,
      isCreditNote: xeroBill?.isCreditNote || extracted.isCreditNote || false,
      xeroCurrency: xeroBill?.currency || null,
      xeroCurrencyRate: xeroBill?.currencyRate ?? null,
      caProvince,
      country: $user?.country,
      splitMode: payload.splitMode === true,
      splitTarget: payload.splitTarget || null,
      splitCostTypes: payload.splitCostTypes || [],
      costGroups: centres.groups
    });
    const response = await fetch('/api/jt/createvendorbill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Failed to create the JobTread vendor bill');
    if (data.results && data.results[0] && data.results[0].success === false) {
      throw new Error(data.results[0].error || 'Failed to create the JobTread vendor bill');
    }
    return data;
  }

  function finishBill(bill) {
    bills = bills.filter(item => item.uploadId !== bill.uploadId);
    awaitingApprovalCount.set(bills.length);
    const nextPartial = { ...partialSync };
    delete nextPartial[bill.uploadId];
    partialSync = nextPartial;
    success = `${bill.extractedData?.vendorName || bill.fileName || 'Bill'} approved and synced.`;
    setTimeout(() => { if (success) success = null; }, 4000);
  }

  async function approveAndSync(bill, allowDuplicate = false) {
    if (syncingId) return;
    if (!bill.reviewPayload) {
      rowError = { ...rowError, [bill.uploadId]: 'Open the full details and sync from there. This bill has no saved review.' };
      return;
    }
    syncingId = bill.uploadId;
    const nextErrors = { ...rowError };
    delete nextErrors[bill.uploadId];
    rowError = nextErrors;
    syncNote = { ...syncNote, [bill.uploadId]: 'Syncing…' };

    try {
      const payload = bill.reviewPayload;
      let xeroResult = partialSync[bill.uploadId]?.xeroResult || null;

      if (isQuickBooks && payload.accountingOnly) {
        syncNote = { ...syncNote, [bill.uploadId]: 'Syncing to QuickBooks…' };
        const qboEnrichment = {};
        qboEnrichment[bill.uploadId] = {
          ...(payload.overheadAccountCode && { overheadAccountId: payload.overheadAccountCode }),
          ...(payload.selectedQboVendorId && { qboVendorId: payload.selectedQboVendorId, qboVendorName: payload.qboVendorName }),
          alreadyPaid: payload.markAsPaid === true,
          ...(payload.qboPaymentAccountId && { paymentAccountId: payload.qboPaymentAccountId })
        };
        const response = await fetch('/api/qbo/sync-overhead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uploadIds: [bill.uploadId], enrichment: qboEnrichment })
        });
        const data = await response.json().catch(() => ({}));
        const result = data.results?.[0];
        if (!response.ok || !result?.success) throw new Error(result?.error || data.error || 'Failed to sync to QuickBooks');
        finishBill(bill);
        return;
      }

      if (!isQuickBooks && !xeroResult) {
        syncNote = { ...syncNote, [bill.uploadId]: 'Syncing to Xero…' };
        const { response, data } = await postXero(bill, allowDuplicate);
        const first = data.results?.[0];
        if (response.ok && first?.duplicateSupplierInvoice && !allowDuplicate) {
          duplicatePrompt = { bill, message: first.error };
          return;
        }
        if (!response.ok || !data.success || !first?.success) {
          throw new Error(first?.error || data.error || 'Failed to sync to Xero');
        }
        xeroResult = first;
        partialSync = { ...partialSync, [bill.uploadId]: { xeroResult } };
      }

      if (!isQuickBooks && payload.accountingOnly) {
        finishBill(bill);
        return;
      }

      syncNote = { ...syncNote, [bill.uploadId]: 'Syncing to JobTread…' };
      await postJobTread(bill, xeroResult?.xeroBill || { id: null, isCreditNote: false });
      finishBill(bill);
    } catch (err) {
      const syncedToXero = !!partialSync[bill.uploadId]?.xeroResult;
      rowError = {
        ...rowError,
        [bill.uploadId]: syncedToXero
          ? `Xero bill created. JobTread was not updated: ${err.message || 'sync failed'}`
          : (err.message || 'Approve and sync failed')
      };
    } finally {
      if (syncingId === bill.uploadId) syncingId = null;
      const notes = { ...syncNote };
      delete notes[bill.uploadId];
      syncNote = notes;
    }
  }

  async function confirmDuplicateSync() {
    const bill = duplicatePrompt?.bill;
    duplicatePrompt = null;
    if (!bill) return;
    await approveAndSync(bill, true);
  }
</script>

<Layout activeRoute="approvals">
  <div class="container mx-auto px-4 py-8 max-w-5xl">
    <h1 class="text-2xl font-bold mb-1">Bill Approvals</h1>
    {#if !approvalsOn}
      <p class="text-gray-600 mb-4">Approvals are turned off currently. Would you like to turn the approval step on?</p>
      <p class="text-gray-600 mb-6">Bills imported via Direct Bill will need to be signed off by an approver before syncing into Xero and JobTread.</p>
      {#if $settingsError}
        <div class="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">{$settingsError}</div>
      {/if}
      <ApprovalStepToggle />
      {#if !canTurnApprovalsOn}
        <p class="text-sm text-gray-500 mt-3">An organisation owner or admin can turn this on.</p>
      {/if}
    {:else}
    <p class="text-gray-600 mb-6">Bills waiting for approval. Check the lines and cost centres, then approve and sync, or open the full review.</p>

    {#if error}
      <div class="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">{error}</div>
    {/if}
    {#if success}
      <div class="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded">{success}</div>
    {/if}

    {#if loading}
      <div class="flex items-center py-8"><div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-3"></div>Loading…</div>
    {:else if bills.length === 0}
      <div class="bg-white rounded-lg shadow p-6 text-gray-600">No bills are waiting for approval.</div>
    {:else}
      <div class="space-y-4">
        {#each bills as bill}
          {@const data = bill.reviewPayload?.extractedData || bill.extractedData || {}}
          {@const lines = allocations[bill.uploadId]}
          {@const overhead = bill.reviewPayload?.accountingOnly === true}
          <div class="bg-white rounded-lg shadow border-l-4 border-orange-400 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <p class="font-semibold text-gray-900">{data.vendorName || bill.fileName}</p>
                <p class="text-sm text-gray-600 mt-0.5">
                  {#if data.invoiceNumber}Invoice {data.invoiceNumber}{/if}
                  {#if jobLabel(bill)}<span> · {jobLabel(bill)}</span>{/if}
                  {#if referenceLabel(bill)}<span> · {referenceLabel(bill)}</span>{/if}
                </p>
                {#if bill.fileName}
                  <p class="text-xs text-gray-500 mt-0.5">{bill.fileName}</p>
                {/if}
                {#if bill.submittedForApprovalAt}
                  <p class="text-xs text-gray-500">Submitted {submittedLabel(bill.submittedForApprovalAt)}{#if bill.uploaderName} · Uploaded by {bill.uploaderName}{/if}</p>
                {/if}
              </div>
              <p class="text-lg font-semibold text-gray-900 tabular-nums flex-shrink-0">
                {data.total != null ? fmt(data.total) : ''}
              </p>
            </div>

            <div class="mt-3 border-t border-gray-100 pt-3">
              {#if !lines}
                <p class="text-sm text-gray-500">Loading line items…</p>
              {:else if lines.length === 0}
                <p class="text-sm text-gray-500">No line items on this bill.</p>
              {:else}
                <div class="space-y-2">
                  {#each lines as line}
                    <div class="flex items-start justify-between gap-3 text-sm">
                      <div class="min-w-0">
                        <p class="text-gray-900">{line.description || 'Line'}</p>
                        <p class="text-xs text-gray-500">{allocationText(line, overhead)}</p>
                      </div>
                      <p class="tabular-nums text-gray-700 flex-shrink-0">{fmt(line.amount || 0)}</p>
                    </div>
                  {/each}
                </div>
              {/if}
            </div>

            {#if rowError[bill.uploadId]}
              <p class="mt-3 text-sm text-red-700">{rowError[bill.uploadId]}</p>
            {/if}
            {#if syncNote[bill.uploadId]}
              <p class="mt-3 text-sm text-gray-600">{syncNote[bill.uploadId]}</p>
            {/if}

            <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                on:click={() => openBill(bill)}
                class="px-3 py-1.5 text-sm font-medium bg-white text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
              >
                See full details
              </button>
              <button
                type="button"
                on:click={() => approveAndSync(bill)}
                disabled={syncingId === bill.uploadId}
                class="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {syncingId === bill.uploadId ? 'Syncing…' : (partialSync[bill.uploadId] ? 'Retry JobTread' : 'Approve & Sync')}
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
    {/if}
  </div>

  {#if duplicatePrompt}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="approve-duplicate-title">
        <h3 id="approve-duplicate-title" class="text-lg font-semibold text-gray-900 mb-2">This invoice is already in Xero</h3>
        <p class="text-sm text-gray-600 mb-4">{(duplicatePrompt.message || '').replace(/ If this really is a different .+?, choose "Create anyway"\./, '')}</p>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            on:click={() => (duplicatePrompt = null)}
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Leave it
          </button>
          <button
            type="button"
            on:click={confirmDuplicateSync}
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Create anyway
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>
